import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PasswordRecoveryModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    const handleClickOutside = (e) => {
      if (modalRef?.current && !modalRef?.current?.contains(e?.target)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    setStep(1);
    setEmail('');
    setError('');
    setLoading(false);
    onClose();
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!email) {
      setError('Email address is required');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    setTimeout(() => {
      setStep(2);
      setLoading(false);
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-start justify-center pt-[8vh] px-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={handleClose}></div>
      <div 
        ref={modalRef}
        className="relative w-full max-w-md bg-card rounded-2xl shadow-elevation-5 p-6 md:p-8 m-4 animate-in fade-in zoom-in duration-200"
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-muted transition-smooth"
          aria-label="Close modal"
        >
          <Icon name="X" size={20} />
        </button>

        {step === 1 ? (
          <div className="space-y-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 mb-4 md:mb-6">
                <Icon name="KeyRound" size={32} color="var(--color-primary)" className="md:w-10 md:h-10" />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">Reset Password</h2>
              <p className="text-sm md:text-base text-muted-foreground">
                Enter your email address and we'll send you instructions to reset your password
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              <Input
                label="Email Address"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e?.target?.value);
                  setError('');
                }}
                error={error}
                required
                disabled={loading}
              />

              <div className="space-y-3">
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  loading={loading}
                  iconName="Send"
                  iconPosition="right"
                >
                  Send Reset Link
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  fullWidth
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </form>

            <div className="p-3 md:p-4 bg-muted rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={18} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                <p className="text-xs md:text-sm text-muted-foreground">
                  The reset link will expire in 1 hour for security purposes. If you don't receive the email, check your spam folder.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-success/10 mb-4 md:mb-6">
                <Icon name="CheckCircle" size={32} color="var(--color-success)" className="md:w-10 md:h-10" />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">Check Your Email</h2>
              <p className="text-sm md:text-base text-muted-foreground mb-4">
                We've sent password reset instructions to
              </p>
              <p className="text-sm md:text-base font-medium text-foreground">{email}</p>
            </div>

            <div className="space-y-4">
              <div className="p-4 md:p-6 bg-muted rounded-lg space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">1</div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Check your inbox</p>
                    <p className="text-xs text-muted-foreground mt-1">Look for an email from security@finvisar.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">2</div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Click the reset link</p>
                    <p className="text-xs text-muted-foreground mt-1">The link will expire in 1 hour</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">3</div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Create new password</p>
                    <p className="text-xs text-muted-foreground mt-1">Choose a strong, unique password</p>
                  </div>
                </div>
              </div>

              <Button
                variant="default"
                fullWidth
                onClick={handleClose}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Login
              </Button>

              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Didn't receive the email?</p>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-sm font-medium text-primary hover:text-secondary transition-smooth"
                >
                  Try another email address
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordRecoveryModal;